import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import toastValidator from '@components/interface/ToastValidator';
import { Product } from '@interfaces/general';
import { useFetchCallback, useEditItem, useDeleteItem, uploadImage } from '@services/services';
import {
  MSG_CREATE_PRD_OK,
  MSG_UPDATE_PRD_OK,
  MSG_UPDATE_PRD_ERROR,
  MSG_DELETE_PRD_ERROR,
  MSG_DELETE_PRD_OK,
  MSG_CREATE_PRD_ERROR,
  MSG_LIST_PRD_ERROR,
  MSG_DUPLICATE_PRD_OK,
  MSG_DUPLICATE_PRD_ERROR,
  MSG_DELETE_PRD,
  MSG_SORT_PRD_OK,
  MSG_SORT_PRD_ERROR,
  MSG_PRD_DISABLED,
} from '@pages/Menu/Products/messages';
import { useProductActions, useProductsFromStore } from '../../../store/actions/products';
import { DELIVERY_MAP, weekDays } from '@utils/constants';

export const defaultProduct: Product = {
  id: '',
  id_categoria: '',
  nombre: '',
  estatus: 'activo',
  imagen: null,
  opciones: [
    {
      id: '',
      estatus: 'activo',
      presentacion: '',
      precio: 0,
      costo_produccion: 0,
    },
  ],
  descripcion: '',
  disponibilidad_por_dias: false,
  dias_disponibles: weekDays,
  available_for: Object.keys(DELIVERY_MAP).map((key) => key as keyof typeof DELIVERY_MAP),
};

export const useGetProducts = () => {
  const products = useProductsFromStore();
  const { initProducts } = useProductActions();
  const { isFetching, call: fetchProducts } = useFetchCallback<Product[]>('get', '/v1/producto');

  useEffect(() => {
    if (!products.length) {
      fetchProducts()
        .then((response) => {
          if (response) initProducts([...response]);
        })
        .catch(() => {
          toast.error(MSG_LIST_PRD_ERROR);
        });
    }
  }, []);

  return { isFetching, products };
};

export const useEditProduct = () => {
  const { updateProduct } = useProductActions();
  const { updatingIds, handleUpdate } = useEditItem<Product>('/v1/producto');
  const handleUpdateProduct = async (item: Product) => {
    try {
      await handleUpdate(item);
      updateProduct(item);
      toast.success(MSG_UPDATE_PRD_OK);
    } catch (err) {
      toast.error(MSG_UPDATE_PRD_ERROR);
    }
  };
  return { updatingProductIds: updatingIds, handleUpdateProduct };
};

export const useDeleteProduct = () => {
  const { deleteProduct } = useProductActions();
  const { deletingIds, handleDelete } = useDeleteItem<Product>('/v1/producto');
  const handleDeleteProduct = (itemId: string) => {
    toastValidator({
      message: MSG_DELETE_PRD,
      onValidate: async () => {
        try {
          await handleDelete(itemId);
          deleteProduct({ id: itemId });
          toast.success(MSG_DELETE_PRD_OK);
        } catch (err) {
          toast.error(MSG_DELETE_PRD_ERROR);
        }
      },
    });
  };
  return { deletingProductIds: deletingIds, handleDeleteProduct };
};

const getMissingProductValues = (product: Product) => {
  const missingValues: (keyof Product)[] = [];

  // Validaciones básicas
  if (!product.nombre) missingValues.push('nombre');
  if (!product.descripcion) missingValues.push('descripcion');
  if (!product.id_categoria) missingValues.push('id_categoria');

  // Validación de opciones
  if (!product.opciones || !product.opciones.length) {
    missingValues.push('opciones');
  } else {
    product.opciones.some((opt) => {
      if (!opt.presentacion || !opt.precio) {
        missingValues.push('opciones');
        return true;
      }
    });
  }

  if (product.disponibilidad_por_dias && (!product.dias_disponibles || product.dias_disponibles.length === 0)) {
    missingValues.push('dias_disponibles');
  }

  return missingValues;
};

export const useProduct = (id?: string) => {
  const { products, isFetching: isFetchingProducts } = useGetProducts();
  const { addProduct, updateProduct } = useProductActions();

  const [product, setProduct] = useState<Product>(defaultProduct);

  const [missingValues, setMissingValues] = useState<(keyof Product)[]>([]);
  const [isDirty, setDirty] = useState(false);

  const { isFetching: isUpdating, call: callUpdate } = useFetchCallback<Product, Product>('put', '/v1/producto');
  const { isFetching: isCreating, call: callCreate } = useFetchCallback<Product, Product>('post', '/v1/producto');

  const isFetching = isUpdating || isCreating;

  useEffect(() => {
    if (id) {
      const prod = products.find((cat) => cat.id === id);
      if (prod) {
        const availableDays = prod.dias_disponibles || weekDays;
        const availableFor =
          prod?.available_for || Object.keys(DELIVERY_MAP).map((key) => key as keyof typeof DELIVERY_MAP);
        setProduct({ ...prod, dias_disponibles: availableDays, available_for: availableFor });
      }
    }
  }, [products]);

  const handleUpdateValue = <K extends keyof Product>(key: K, value: Product[K]) => {
    const newPrd = { ...product, [key]: value };
    if (missingValues.includes(key)) setMissingValues(getMissingProductValues(newPrd));
    if (key === 'opciones' && (value as Product['opciones'])?.every((opt) => opt.estatus === 'inactivo'))
      toast(MSG_PRD_DISABLED, {
        icon: '⚠️',
        duration: 4000,
        style: {
          background: '#3D5A73',
          color: '#fff',
        },
      });
    setDirty(true);
    setProduct(newPrd);
  };

  const handleSubmitChanges = async () => {
    const values = getMissingProductValues(product);

    if (values.length) {
      setMissingValues(values);

      if (values.includes('dias_disponibles')) {
        toast.error('Debes seleccionar al menos un día cuando la disponibilidad personalizada está activa.');
      }

      return false;
    }
    if (product.imagen && !product.imagen.startsWith('https://')) {
      const imageUrl = await uploadImage(product.imagen);
      if (imageUrl) product.imagen = imageUrl;
    }
    if (product.id) {
      try {
        const fixedProduct: Product = {
          ...product,
          dias_disponibles: product.dias_disponibles,
        };

        await callUpdate({ data: fixedProduct, path: id });
        updateProduct(product);
        toast.success(MSG_UPDATE_PRD_OK);
        setDirty(false);
      } catch (err) {
        toast.error(MSG_UPDATE_PRD_ERROR);
        return false;
      }
    } else {
      try {
        const result = await callCreate({ data: product });
        addProduct(result);
        toast.success(MSG_CREATE_PRD_OK);
        setDirty(false);
      } catch (err) {
        toast.error(MSG_CREATE_PRD_ERROR);
        return false;
      }
    }
    return true;
  };

  return {
    product,
    setProduct,
    isFetchingGet: isFetchingProducts,
    isDirty,
    isFetchingEdition: isFetching,
    missingValues,
    handleUpdateValue,
    handleSubmitChanges,
  };
};

export const useDuplicateProduct = () => {
  const { products } = useGetProducts();
  const { addProduct } = useProductActions();

  const [duplicatingProductIds, setDuplicatingProductIds] = useState<string[]>([]);

  const { call: callDuplicate } = useFetchCallback<Product, Product>('post', '/v1/producto');

  const handleDuplicate = async (productId: string) => {
    const newIds = [...duplicatingProductIds, productId];
    setDuplicatingProductIds(newIds.slice());
    const product = products.find((prd) => prd.id === productId);
    if (product) {
      try {
        const result = await callDuplicate({
          data: { ...product, nombre: product.nombre + ' (copia)', id: '', estatus: 'inactivo', imagen: null },
        });
        addProduct(result);
        toast.success(MSG_DUPLICATE_PRD_OK);
      } catch (err) {
        toast.error(MSG_DUPLICATE_PRD_ERROR);
      }
    }
    newIds.splice(newIds.indexOf(productId), 1);
    setDuplicatingProductIds(newIds.slice());
  };

  return { handleDuplicate, duplicatingProductIds };
};

export const useSortProducts = () => {
  const { products, isFetching } = useGetProducts();
  const { initProducts } = useProductActions();
  const [sortedProducts, setSortedProducts] = useState(products);

  useEffect(() => {
    setSortedProducts(products);
  }, [products]);

  const { isFetching: isUpdating, call: callUpdate } = useFetchCallback<
    { items: { id: string; orden: number }[] },
    undefined
  >('put', '/v1/producto');
  const handleUpdateOrder = (sourceIndex: number, destinationIndex: number) => {
    const orderedProducts = sortedProducts.slice();
    const removedItem = orderedProducts.splice(sourceIndex, 1)[0];
    orderedProducts.splice(destinationIndex, 0, removedItem);
    setSortedProducts(orderedProducts);
  };
  const handleSubmitChanges = async () => {
    try {
      await callUpdate({ data: { items: sortedProducts.map((cat, idx) => ({ id: cat.id, orden: idx + 1 })) } });
      initProducts(sortedProducts.slice());
      toast.success(MSG_SORT_PRD_OK);
      return true;
    } catch (error) {
      if (error) toast.error(MSG_SORT_PRD_ERROR);
      return false;
    }
  };
  return { sortedProducts, isFetching, isUpdating, handleUpdateOrder, handleSubmitChanges };
};
